import React, { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { Button } from '@xsolla/ui-core';

import {
  createOAuthConnectionFields,
  deleteOAuthConnectionFields,
  getOAuthConnection,
  updateOAuthConnectionFields
} from '../../../api/configurations/oauth2connection';
import { OAuthConnectionFieldsType } from '../../../api/configurations/oauth2connection/types';
import { AccountManager } from '../../../api/merchant/types';
import { Error } from '../../../api/setup/getApiError';
import { DEFAULT_ACCOUNT_MANAGER, DEFAULT_CUSTOM_OAUTH2_COLOR } from '../../../constants';
import { useConfig } from '../../../contexts/config';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { HorizontalCenterBlock } from '../../../styled';
import { FormFooter, HeaderTitle, Preloader, Separator, ToDashboardError } from '../../ui';
import { isStringJson } from '../../UserDatabase/UserAttributesSchema/utils';
import CustomizationFields from './CustomizationFields';
import { EnterpriseFields } from './EnterpriseFields';
import { MappingFields } from './MappingFields';
import {
  addScope,
  changeBackgroundColor,
  changeLogoUrl,
  deleteScope,
  touchLogoUrl
} from './mutators';
import { SettingsFields } from './SettingsFields';
import { BlockWrapper, NotificationLink, Wrapper } from './style';
import { defaultSettingsObject } from './utils';
import { Oauth2ConnectionValidator } from './validator';

type Props = RouteComponentProps & {
  projectId: string;
};

const EnterpriseConnectionsForm = ({ history, projectId }: Props): JSX.Element => {
  const { navigationPath } = usePaths();
  const intl = useIntl();
  const { showErrorNotify, showSuccessNotify } = useNotifications();
  const { xsollaAnalytics } = useConfig();

  // ToDo: implement React reducer
  const [oAuthFields, setOAuthFields] = useState<OAuthConnectionFieldsType | null>(null);
  const [fetching, setFetching] = useState<boolean>(true);
  const [fetchingSubmit, setFetchingSubmit] = useState<boolean>(false);
  const [newConnection, setNewConnection] = useState<boolean>(false);
  const [errorData, setErrorData] = useState<Error | null>(null);
  const [errorSubmit, setErrorSubmit] = useState<boolean>(false);
  const [accountManager] = useState<AccountManager>(DEFAULT_ACCOUNT_MANAGER);
  const [schema, setSchema] = useState<string>('');

  useEffect(() => {
    getOAuthConnection(projectId)
      .then((res) => {
        setOAuthFields(res);
        if (Object.keys(res).length === 0) {
          setNewConnection(true);
        }
      })
      .catch((error) => {
        setErrorData(error);
      })
      .finally(() => {
        setFetching(false);
      });
  }, []);

  const onSubmit = async (values: OAuthConnectionFieldsType) => {
    const payload = {
      ...values,
      map_email: values.map_email ? values.map_email : '',
      map_id: values.map_id ? values.map_id : '',
      map_firstname: values.map_firstname ? values.map_firstname : '',
      map_lastname: values.map_lastname ? values.map_lastname : '',
      map_nickname: values.map_nickname ? values.map_nickname : '',
      map_birthday: values.map_birthday ? values.map_birthday : '',
      map_attributes: values.map_attributes ? values.map_attributes : '',
      map_partner_data: values.map_partner_data ? values.map_partner_data : '',
      settings: isStringJson(schema) ? JSON.parse(schema) : defaultSettingsObject
    };

    try {
      setErrorSubmit(false);
      setFetchingSubmit(true);
      if (newConnection) {
        await createOAuthConnectionFields(projectId, payload);
        xsollaAnalytics.sendClick(
          'login_pa_module__oauth2_connection_page__create_connection',
          'success'
        );
      } else {
        xsollaAnalytics.sendClick(
          'login_pa_module__oauth2_connection_page__update_connection',
          'success'
        );
        await updateOAuthConnectionFields(projectId, payload);
      }

      showSuccessNotify();
      setNewConnection(false);
    } catch (error) {
      showErrorNotify(error);
      setErrorSubmit(true);
      xsollaAnalytics.sendClick(
        `login_pa_module__oauth2_connection_page__${
          newConnection ? 'create_connection' : 'update_connection'
        }`,
        'error',
        {
          error: error.description || ''
        }
      );
    } finally {
      setFetchingSubmit(false);
    }
  };

  const onDelete = async () => {
    try {
      setFetching(true);
      await deleteOAuthConnectionFields(projectId);
      setOAuthFields(null);
      showSuccessNotify();
      setNewConnection(true);
      xsollaAnalytics.sendClick(
        'login_pa_module__oauth2_connection_page__delete_connection',
        'success'
      );
    } catch (error) {
      showErrorNotify(error);
      xsollaAnalytics.sendClick(
        `login_pa_module__oauth2_connection_page__delete_connection`,
        'error',
        {
          error: error.description || ''
        }
      );
    } finally {
      setFetching(false);
    }
  };

  const backToNavigation = () => history.push({ pathname: navigationPath(projectId) });

  if (fetching) {
    return <Preloader />;
  }

  if (errorData) {
    return <ToDashboardError pageName="oauth2_login_connection" error={errorData} />;
  }

  return (
    <>
      <Form
        keepDirtyOnReinitialize
        onSubmit={onSubmit}
        initialValues={{
          client_id: oAuthFields?.client_id || '',
          client_secret: oAuthFields?.client_secret || '',
          map_email: oAuthFields?.map_email || '',
          map_id: oAuthFields?.map_id || '',
          map_firstname: oAuthFields?.map_firstname || '',
          map_lastname: oAuthFields?.map_lastname || '',
          map_nickname: oAuthFields?.map_nickname || '',
          map_birthday: oAuthFields?.map_birthday || '',
          map_attributes: oAuthFields?.map_attributes || '',
          map_partner_data: oAuthFields?.map_partner_data || '',
          name_prefix: oAuthFields?.name_prefix || '',
          scope: oAuthFields?.scope || [],
          url_access_token: oAuthFields?.url_access_token || '',
          url_authorize: oAuthFields?.url_authorize || '',
          url_resource_owner_details: oAuthFields?.url_resource_owner_details || '',
          background_color: oAuthFields?.background_color || DEFAULT_CUSTOM_OAUTH2_COLOR,
          logo_url: oAuthFields?.logo_url || '',
          display_name: oAuthFields?.display_name || ''
        }}
        validate={Oauth2ConnectionValidator}
        mutators={{ addScope, deleteScope, changeBackgroundColor, changeLogoUrl, touchLogoUrl }}>
        {({ handleSubmit, pristine, form: { mutators } }) => {
          return (
            <>
              <Wrapper>
                <BlockWrapper>
                  <HeaderTitle
                    title={intl.formatMessage({ id: 'enterprise-connection.settings.title' })}
                    subtitle={intl.formatMessage({ id: 'enterprise-connection.settings.subtitle' })}
                    notification={intl.formatMessage(
                      {
                        id: 'enterprise-connection.settings.notification'
                      },
                      {
                        a: (chunk: string) => (
                          <NotificationLink href={`mailto:${accountManager.email}`}>
                            {chunk}
                          </NotificationLink>
                        )
                      }
                    )}
                  />

                  <EnterpriseFields />
                </BlockWrapper>

                <Separator />

                <BlockWrapper>
                  <MappingFields />
                </BlockWrapper>

                <Separator />

                <SettingsFields oAuthFieldsSettings={oAuthFields?.settings} setSchema={setSchema} />

                <Separator />

                <CustomizationFields />

                <Separator />

                <HorizontalCenterBlock>
                  <Button
                    data-test-id="oauth2-connection-delete-button"
                    look="link"
                    palette="danger"
                    size="md"
                    disabled={newConnection}
                    onClick={onDelete}>
                    {intl.formatMessage({ id: 'enterprise-connection.settings.delete' })}
                  </Button>
                </HorizontalCenterBlock>
              </Wrapper>

              <FormFooter
                onSubmit={() => {
                  mutators.touchLogoUrl();
                  handleSubmit();
                }}
                onCancel={backToNavigation}
                fetching={fetchingSubmit}
                error={errorSubmit}
              />
            </>
          );
        }}
      </Form>
    </>
  );
};

export default withRouter(EnterpriseConnectionsForm);
